"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Text, Text as MantineText } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { useItemsStore } from "@/stores/itemsStore";
import { useAriaAnnouncerStore } from "@/stores/ariaAnnouncerStore";
import styles from "./styles.module.scss";

const formatRollingDigits = (digitCount: number) => {
  const digits = Array.from({ length: digitCount }, () => Math.floor(Math.random() * 10)).join("");

  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const ItemCount: React.FC = () => {
  const itemTotal = useItemsStore((state) => state.itemTotal);
  const totalLoading = useItemsStore((state) => state.totalLoading);
  const reduceMotion = useReducedMotion();
  const { announce } = useAriaAnnouncerStore((state) => state.actions);
  const previousTotal = useRef<number>(0);
  const formattedTotal = useMemo(() => itemTotal.toLocaleString(), [itemTotal]);
  const digitCount = Math.max(itemTotal.toString().length, 1);
  const countIsReloading = totalLoading && itemTotal > 0;
  const shouldRollCount = countIsReloading && !reduceMotion;
  const [rollingCount, setRollingCount] = useState(formattedTotal);

  useEffect(() => {
    if (!shouldRollCount) {
      setRollingCount(formattedTotal);
      return;
    }

    setRollingCount(formatRollingDigits(digitCount));
    const interval = window.setInterval(() => {
      setRollingCount(formatRollingDigits(digitCount));
    }, 75);

    return () => window.clearInterval(interval);
  }, [digitCount, formattedTotal, shouldRollCount]);

  useEffect(() => {
    if (itemTotal <= 0 || shouldRollCount) {
      if (itemTotal <= 0) {
        previousTotal.current = 0;
      }
      return;
    }

    if (previousTotal.current > 0 && itemTotal !== previousTotal.current) {
      announce(`Search updated, showing ${itemTotal.toLocaleString()} items`);
    }
    previousTotal.current = itemTotal;
  }, [itemTotal, shouldRollCount, announce]);

  if (itemTotal <= 0) {
    return null;
  }

  return (
    <Text
      size="xs"
      aria-busy={countIsReloading}
      aria-label={countIsReloading ? "Updating item count" : `${formattedTotal} items found`}
    >
      <MantineText
        span
        aria-hidden="true"
        className={`${styles.count} ${shouldRollCount ? styles.rolling : ""}`}
        style={{
          minWidth: `${Math.max(formattedTotal.length, rollingCount.length)}ch`,
          fontWeight: shouldRollCount ? 400 : 600,
        }}
      >
        {shouldRollCount ? rollingCount : formattedTotal}
      </MantineText>{" "}
      results
    </Text>
  );
};

export default ItemCount;
